import React, { useEffect, useRef, useState } from 'react';
import { PdsButton, PdsHeading, PdsTextPassage } from '@principal/design-system-react';
import { PdsIconClock } from '@principal/design-system-icons-react';
import { useGuestAuth } from './GuestAuthProvider';
import './GuestSessionBanner.css';

const IDLE_TIMEOUT_MINUTES = 360;
const IDLE_TIMEOUT_MILLISECONDS = IDLE_TIMEOUT_MINUTES * 60 * 1000;
const IDLE_CHECK_INTERVAL_MS = 30_000;
const COUNTDOWN_SECONDS = 120;
const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
    'mousemove',
    'keydown',
    'click',
    'scroll',
    'touchstart',
];
const SESSION_BANNER_TITLE = 'Still there? Your session is about to end due to inactivity.';
const SESSION_BANNER_BODY_PREFIX = "To keep your account secure, this session will automatically end in ";
const SESSION_BANNER_TIME_SUFFIX = 'minutes';
const CONTINUE_SESSION_LABEL = 'Continue session';
const EXIT_LABEL = 'Exit';

const formatCountdown = (totalSeconds: number) => {
    const safeSeconds = Math.max(totalSeconds, 0);
    const minutes = Math.floor(safeSeconds / 60);
    const seconds = safeSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const GuestSessionBanner = () => {
    const { guestAuth, isLoggedIn, sessionTimedOut, extendSession } = useGuestAuth();
    const dialogRef = useRef<HTMLDialogElement>(null);
    const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
    const idleTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const lastActivityAtRef = useRef(Date.now());
    const warningModalOpenRef = useRef(false);
    const timeoutTokenRef = useRef<string | undefined>(undefined);

    const clearIdleTimer = () => {
        if (idleTimerRef.current) {
            clearInterval(idleTimerRef.current);
            idleTimerRef.current = null;
        }
    };

    const clearCountdownTimer = () => {
        if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
        }
    };

    const onActivity = () => {
        if (warningModalOpenRef.current) {
            return;
        }
        lastActivityAtRef.current = Date.now();
    };

    const addActivityListeners = () => {
        ACTIVITY_EVENTS.forEach((eventName) => {
            if (eventName === 'scroll' || eventName === 'touchstart') {
                window.addEventListener(eventName, onActivity, { passive: true });
                return;
            }
            window.addEventListener(eventName, onActivity);
        });
    };

    const removeActivityListeners = () => {
        ACTIVITY_EVENTS.forEach((eventName) => {
            window.removeEventListener(eventName, onActivity);
        });
    };

    const showWarningModal = () => {
        warningModalOpenRef.current = true;
        timeoutTokenRef.current = guestAuth?.token;
        clearIdleTimer();
        removeActivityListeners();
        setCountdown(COUNTDOWN_SECONDS);
        dialogRef.current?.showModal();
        countdownTimerRef.current = setInterval(() => {
            setCountdown((prev) => prev - 1);
        }, 1000);
    };

    const checkForIdleTimeout = (source: 'interval' | 'visibilitychange' | 'focus') => {
        if (warningModalOpenRef.current) {
            return;
        }
        const nowMs = Date.now();
        const idleForMs = nowMs - lastActivityAtRef.current;
        if (idleForMs >= IDLE_TIMEOUT_MILLISECONDS) {
            showWarningModal();
        }
    };

    const onVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
            checkForIdleTimeout('visibilitychange');
        }
    };

    const onWindowFocus = () => {
        checkForIdleTimeout('focus');
    };

    const addVisibilityListeners = () => {
        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('focus', onWindowFocus);
    };

    const removeVisibilityListeners = () => {
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('focus', onWindowFocus);
    };

    const closeModalSafely = () => {
        try {
            dialogRef.current?.close();
        } catch {
        }
    };

    const startIdleTimer = () => {
        clearIdleTimer();
        warningModalOpenRef.current = false;
        timeoutTokenRef.current = undefined;
        lastActivityAtRef.current = Date.now();
        idleTimerRef.current = setInterval(() => {
            checkForIdleTimeout('interval');
        }, IDLE_CHECK_INTERVAL_MS);
    };

    const handleContinue = async (event?: { preventDefault?: () => void }) => {
        const refreshed = await extendSession();
        if (!refreshed) {
            return;
        }
        warningModalOpenRef.current = false;
        timeoutTokenRef.current = undefined;
        clearCountdownTimer();
        closeModalSafely();
        startIdleTimer();
        addActivityListeners();
        addVisibilityListeners();
    };

    const handleTimeout = (reason: 'cancel' | 'countdown', event?: { preventDefault?: () => void }) => {
        clearCountdownTimer();
        clearIdleTimer();
        warningModalOpenRef.current = false;
        removeActivityListeners();
        removeVisibilityListeners();
        closeModalSafely();
        sessionTimedOut(timeoutTokenRef.current);
    };

    useEffect(() => {
        const dialogElement = dialogRef.current;
        if (!dialogElement) {
            return;
        }
        const preventCancel = (event: Event) => {
            event.preventDefault();
        };
        dialogElement.addEventListener('cancel', preventCancel);
        return () => {
            dialogElement.removeEventListener('cancel', preventCancel);
        };
    }, []);

    useEffect(() => {
        if (countdown < 1) {
            handleTimeout('countdown');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countdown]);

    useEffect(() => {
        if (isLoggedIn) {
            startIdleTimer();
            addActivityListeners();
            addVisibilityListeners();
        } else {
            clearIdleTimer();
            clearCountdownTimer();
            warningModalOpenRef.current = false;
            removeActivityListeners();
            removeVisibilityListeners();
            closeModalSafely();
        }
        return () => {
            clearIdleTimer();
            clearCountdownTimer();
            warningModalOpenRef.current = false;
            removeActivityListeners();
            removeVisibilityListeners();
            closeModalSafely();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    return (
        <dialog
            ref={dialogRef}
            id="guest-session-timeout-modal"
            className="guest-session-banner__modal"
            aria-labelledby="guest-session-timeout-heading"
        >
            <PdsTextPassage lineLength="none" className="guest-session-banner__text-passage">
                <PdsHeading
                    id="guest-session-timeout-heading"
                    className="guest-session-banner__heading"
                    variant="headline-default"
                    headingTag="h1"
                >
                    <PdsIconClock size="xxl" className="guest-session-banner__icon" />
                    <span>{SESSION_BANNER_TITLE}</span>
                </PdsHeading>
                <p className="guest-session-banner__text">
                    {SESSION_BANNER_BODY_PREFIX}
                    <mark className="guest-session-banner__time-remaining">{formatCountdown(countdown)}</mark>{' '}
                    <span aria-hidden="true">{SESSION_BANNER_TIME_SUFFIX}.</span>
                </p>
            </PdsTextPassage>
            <div className="guest-session-banner__footer">
                <PdsButton type="button" onClick={handleContinue}>
                    {CONTINUE_SESSION_LABEL}
                </PdsButton>
                <br />
                <PdsButton type="button" link="emphasis-inverted" onClick={(event: Event) => handleTimeout('cancel', event)}>
                    {EXIT_LABEL}
                </PdsButton>
            </div>
        </dialog>
    );
};

export default GuestSessionBanner;
